import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Loader2, ShieldCheck, FileJson, FileSpreadsheet, Database, Cookie, HardDrive, Eraser } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authApi, legalApi } from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/format";
import { downloadMyData } from "@/lib/dataExport";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function DataInventoryCard() {
  const [inv, setInv] = useState(null);
  useEffect(() => { legalApi.dataInventory().then((r) => setInv(r.data)).catch(() => {}); }, []);
  if (!inv) return null;
  return (
    <Card className="space-y-3 p-5" data-testid="privacy-inventory">
      <div className="flex items-start gap-3">
        <Database className="mt-0.5 h-5 w-5 text-slate-500" />
        <div>
          <h3 className="font-semibold text-slate-800">What we store</h3>
          <p className="mt-1 text-sm text-slate-500">A plain-English inventory of what FD Check keeps on your device and on our servers.</p>
        </div>
      </div>
      <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
        <Cookie className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span data-testid="privacy-cookie-statement">{inv.cookies_statement}</span>
      </div>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="device">
          <AccordionTrigger className="text-sm" data-testid="privacy-inv-device">
            <span className="flex items-center gap-2"><HardDrive className="h-4 w-4 text-slate-400" /> On your device ({inv.device_storage.length})</span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {inv.device_storage.map((d, i) => (
                <div key={i} className="rounded-md border border-slate-100 p-2.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">{d.key}</span>
                    <span className={`rounded px-1.5 py-0.5 text-xs ${d.essential ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{d.essential ? "Essential" : "Optional"}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{d.purpose}</p>
                  <p className="mt-0.5 text-[11px] text-slate-400">{d.where}</p>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="server">
          <AccordionTrigger className="text-sm" data-testid="privacy-inv-server">
            <span className="flex items-center gap-2"><Database className="h-4 w-4 text-slate-400" /> On our servers ({inv.server_data.length})</span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {inv.server_data.map((d, i) => (
                <div key={i} className="rounded-md border border-slate-100 p-2.5 text-sm">
                  <div className="font-medium text-slate-700">{d.category}</div>
                  <p className="mt-0.5 text-xs text-slate-500">{d.purpose}</p>
                  <p className="mt-1 text-[11px] text-slate-400"><strong>Legal basis:</strong> {d.legal_basis} · <strong>Retention:</strong> {d.retention}</p>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

function RetentionCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await legalApi.retentionPreview(); setPreview(r.data); }
    catch (e) { toast.error(apiError(e, "Could not load retention preview")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const chosen = Object.keys(selected).filter((k) => selected[k]);

  const doPurge = async () => {
    setPurging(true);
    try {
      const r = await legalApi.retentionPurge(chosen);
      toast.success(`Purged ${r.data.total} record${r.data.total === 1 ? "" : "s"}`);
      setSelected({});
      await load();
    } catch (e) { toast.error(apiError(e, "Could not run purge")); }
    finally { setPurging(false); }
  };

  return (
    <Card className="space-y-4 p-5" data-testid="privacy-retention">
      <div className="flex items-start gap-3">
        <Eraser className="mt-0.5 h-5 w-5 text-slate-500" />
        <div>
          <h3 className="font-semibold text-slate-800">Data retention</h3>
          <p className="mt-1 text-sm text-slate-500">
            Clear down old archived and activity-log data. Live inspections, buildings, doors and
            reports are <strong>never</strong> touched — only data you&apos;ve already archived or pure
            activity logs. This preview deletes nothing until you confirm.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Scanning…</div>
      ) : preview ? (
        <>
          <div className="space-y-2">
            {preview.categories.map((c) => (
              <label key={c.key} className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3" data-testid={`retention-cat-${c.key}`}>
                <Checkbox checked={!!selected[c.key]} onCheckedChange={() => toggle(c.key)} disabled={c.count === 0} data-testid={`retention-check-${c.key}`} className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">{c.label}</span>
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${c.count ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`} data-testid={`retention-count-${c.key}`}>{c.count} eligible</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{c.description}</p>
                  <p className="mt-1 text-[11px] text-slate-400">Older than {c.retention_days} days{c.oldest ? ` · oldest ${new Date(c.oldest).toLocaleDateString()}` : ""}</p>
                </div>
              </label>
            ))}
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50" disabled={chosen.length === 0 || purging} data-testid="retention-purge-start">
                {purging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Run purge ({chosen.reduce((n, k) => n + (preview.categories.find((c) => c.key === k)?.count || 0), 0)})
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="retention-purge-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Permanently remove this data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes the selected archived / activity-log records for your
                  company. It cannot be undone. Live safety records are not affected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={doPurge} className="bg-amber-600 hover:bg-amber-700" data-testid="retention-purge-confirm">Yes, purge</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : null}
    </Card>
  );
}

export default function PrivacyDataTab() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!(user?.is_company_admin || user?.is_platform_admin);
  const [exporting, setExporting] = useState("");
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try { await downloadMyData(fmt); toast.success("Your data download has started"); }
    catch (e) { toast.error(apiError(e, "Could not export your data")); }
    finally { setExporting(""); }
  };

  const doDelete = async () => {
    if (confirm.trim().toUpperCase() !== "DELETE") { toast.error("Type DELETE to confirm."); return; }
    if (!password) { toast.error("Enter your password."); return; }
    setBusy(true);
    try {
      await authApi.deleteAccount({ confirmation: confirm.trim().toUpperCase(), password });
      toast.success("Your account has been deleted");
      logout();
      navigate("/login", { replace: true });
    } catch (e) { toast.error(apiError(e, "Could not delete account")); setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="privacy-data-tab">
      <Card className="space-y-4 p-5" data-testid="privacy-export">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 text-slate-500" />
          <div>
            <h3 className="font-semibold text-slate-800">Download my data</h3>
            <p className="mt-1 text-sm text-slate-500">
              Get a copy of the personal data FD Check holds about you, plus a summary of the
              inspections, quotes and jobs you&apos;ve created. Inspection records are kept for the
              fire-safety audit trail even after you leave, but are re-labelled so they no longer
              identify you.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => doExport("json")} disabled={!!exporting} data-testid="privacy-export-json">
            {exporting === "json" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileJson className="mr-2 h-4 w-4" />} Download JSON
          </Button>
          <Button variant="outline" onClick={() => doExport("csv")} disabled={!!exporting} data-testid="privacy-export-csv">
            {exporting === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Download CSV
          </Button>
        </div>
      </Card>

      <DataInventoryCard />

      {isAdmin && <RetentionCard />}

      <Card className="space-y-3 border-red-100 p-5" data-testid="privacy-delete">
        <h3 className="font-semibold text-red-700">Delete my account</h3>
        <p className="text-sm text-slate-500">
          Permanently close your account and remove your personal details. If you are the last
          user on the company, the company is closed too. This cannot be undone.
        </p>
        {!showDelete ? (
          <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setShowDelete(true)} data-testid="privacy-delete-start">
            <Trash2 className="mr-2 h-4 w-4" /> Delete my account
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Type DELETE to confirm</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="privacy-delete-confirm" />
            </div>
            <div className="space-y-1.5">
              <Label>Your password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="privacy-delete-password" />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowDelete(false)} disabled={busy}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={doDelete} disabled={busy} data-testid="privacy-delete-confirm-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Permanently delete
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
